import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Post } from "@circle-react/types/Post";
import { useQuery, useQueryClient } from "react-query";

const REQUEST_KEY = "post-summary";

interface UsePostSummaryProps {
  post: Post;
}

interface AiSummary {
  content: string;
  overview?: string;
}

export const usePostSummary = ({ post }: UsePostSummaryProps) => {
  const queryKey = [REQUEST_KEY, post.id];
  const queryClient = useQueryClient();
  const { data, isInitialLoading, refetch } = useQuery<AiSummary>({
    queryKey: queryKey,
    queryFn: () =>
      reactQueryGet(
        internalApi.posts.summary({
          postId: post.id,
        }),
      ),
    enabled: false, // Don't auto-fetch when mounted
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (_, error: any) => error?.status !== 404,
  });

  const reset = () => {
    if (isInitialLoading) {
      void queryClient.cancelQueries({ queryKey });
    }
    void queryClient.resetQueries({ queryKey, exact: true });
  };

  const toggleSummary = () => {
    if (data || isInitialLoading) {
      void reset();
    } else {
      void refetch();
    }
  };

  return {
    summary: data,
    isLoadingSummary: isInitialLoading,
    refetchSummary: refetch,
    clearSummary: reset,
    toggleSummary,
  };
};
